'use client';

import { useTheme } from '@mui/material';
import { ReactElement } from 'react';
import SegmentedMeter from './SegmentedMeter';
import { BIOMETRIC_LADDER_STEPS, biometricLadderPositionOf } from './ladder';
import { TBiometricConfidenceLevel } from './types';

export type TBiometricConfidenceMeterProps = {
  level: TBiometricConfidenceLevel;
  /** Defaults to the narrower width the design gives this axis on a check card. */
  maxWidth?: string | number;
  /** Omit to leave the bars decorative, which is right wherever the level is also stated in text. */
  'aria-label'?: string;
};

/**
 * The biometric confidence ladder: **two steps**, not four.
 *
 * The axis reaches three levels above zero, `InProgress`, `Low` and `High`, but the first two share
 * the same one-of-two position and differ only in tone, so two steps is all the ladder needs. A full meter therefore means "as good as this check gets", the same thing a full
 * document meter means, even though the two are drawn with different numbers of steps.
 *
 * Because the ladders differ, a level name shared with the document axis does not share a position:
 * `High` is two of two here and three of four there. That is exactly why this is its own component
 * rather than an `axis` prop, and why the ladder tables are per axis.
 *
 * The narrower default width is the design's too: two steps at the same step size occupy half the
 * room four do, so this defaults to `4rem` where the document meter defaults to `8rem`.
 *
 * **Takes no `data-testid`**, for the same reason its document counterpart does not: it is always
 * drawn inside a card or dialog that carries one, and per ADR 72 it is reached from that ancestor.
 */
export default function BiometricConfidenceMeter({
  level,
  maxWidth,
  'aria-label': ariaLabel,
}: Readonly<TBiometricConfidenceMeterProps>): ReactElement {
  const theme = useTheme();
  const { filled, tone } = biometricLadderPositionOf(level);

  return (
    <SegmentedMeter
      bars={BIOMETRIC_LADDER_STEPS}
      filled={filled}
      tone={tone}
      maxWidth={maxWidth ?? theme.semantic.size['4xs']}
      aria-label={ariaLabel}
    />
  );
}
